import React, { useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaExclamationCircle, FaCheckCircle, FaUpload } from 'react-icons/fa';
import { reportUser } from '../../Services/User/Chat';
import { showToast } from '../Toast/toast';
import CustomButton from '../CustomButton';
import './ReportUserModal.css';

const ReportUserModal = ({ show, onHide, userName, userId }) => {
  const [step, setStep] = useState(1); // 1: Confirmation, 2: Reason Form, 3: Success
  const [reason, setReason] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        showToast('Invalid file type. Please upload PDF, Docx, or JPG files only.', 'error');
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirm = () => {
    setStep(2);
  };

  const handleCancel = () => {
    setStep(1);
    setReason('');
    setSelectedFile(null);
    onHide();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('Please enter a report reason', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('reported_type', 'user');
      formData.append('reportable_id', userId);
      formData.append('reason', reason);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await reportUser(formData);
      
      if (response.status) {
        setStep(3);
      } else {
        showToast(response.message || 'Failed to submit report', 'error');
      }
    } catch (error) {
      console.error('Error reporting user:', error);
      showToast(error.message || 'Failed to submit report', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setStep(1);
    setReason('');
    setSelectedFile(null);
    onHide();
  };

  const renderConfirmationStep = () => (
    <div className="report-modal-content">
      <div className="report-icon-wrapper">
        <FaExclamationCircle className="report-icon" />
      </div>
      <h4 className="report-modal-title">Report User</h4>
      <p className="report-modal-question">
        Are you sure you want to report <strong>{userName || 'this user'}</strong>?
      </p>
      <div className="report-modal-buttons">
        <CustomButton
          onClick={handleConfirm}
        //   className="report-yes-btn"
        >
          Yes
        </CustomButton>
        <CustomButton
          onClick={handleCancel}
          variant="secondary"
          className="secondaryButton"
        >
          No
        </CustomButton>
      </div>
    </div>
  );

  const renderReasonStep = () => (
    <div className="report-modal-content">
      <h4 className="report-modal-title">Reason</h4>
      
      <div className="report-form-group">
        <label className="report-label">
          Report Reason <span className="required-asterisk">*</span>
        </label>
        <textarea
          className="report-textarea"
          placeholder="Write here"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={5}
        />
      </div>

      <div className="report-form-group">
        <label className="report-label">Upload Files</label>
        <div className="file-upload-area" onClick={() => fileInputRef.current?.click()}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
          />
          <FaUpload className="upload-icon" />
          <p className="upload-text">Upload Files (PDF, Docx, JPG)</p>
          {selectedFile && (
            <div className="selected-file-info">
              <span>{selectedFile.name}</span>
              <button
                type="button"
                className="remove-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="report-form-buttons">
        <CustomButton
          onClick={handleSubmit}
          className="report-submit-btn"
          disabled={submitting || !reason.trim()}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </CustomButton>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="report-modal-content">
      <div className="success-icon-wrapper">
        <FaCheckCircle className="success-icon" />
      </div>
      <h4 className="report-modal-title">Successful</h4>
      <p className="success-message">
        {userName || 'The user'} has been reported. Your report against this user has been submitted to the admin.
      </p>
      <div className="report-form-buttons">
        <CustomButton
          onClick={handleSuccessClose}
          className="report-ok-btn"
        >
          Ok
        </CustomButton>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={handleCancel} centered className="report-user-modal">
      <Modal.Body className="report-modal-body">
        <button className="modal-close-btn" onClick={handleCancel}>
          <FaTimes />
        </button>
        
        {step === 1 && renderConfirmationStep()}
        {step === 2 && renderReasonStep()}
        {step === 3 && renderSuccessStep()}
      </Modal.Body>
    </Modal>
  );
};

export default ReportUserModal;

